import { HoursDaysWeeks } from "@/interfaces/dates.interface";
import { PriceItemInterface, PricesInterface } from "@/interfaces/prices.interface";
import timeRanges from "@/assets/timeRanges.json";

const findPricesInPriceEntries = (priceList: PriceItemInterface[], searchString: string) => {
  const foundPrice = priceList.find((item) => {
    return item.parkdauer === searchString;
  });

  return foundPrice;
};

const addPrices = (currentPrices: PricesInterface, priceToAdd: PricesInterface) => {
  currentPrices.p1 = currentPrices.p1 + (Number(priceToAdd.p1) ? priceToAdd.p1 : 0);
  currentPrices.p3 = currentPrices.p3 + (Number(priceToAdd.p3) ? priceToAdd.p3 : 0);
  currentPrices.parkhaus = currentPrices.parkhaus + (Number(priceToAdd.parkhaus) ? priceToAdd.parkhaus : 0);
};

export const getAnnualPlanPrice = (priceList: PriceItemInterface[]) => {
  const annualPlanPrice = findPricesInPriceEntries(priceList, "1y");
  if (!annualPlanPrice) throw "Angebot nicht verfügbar";
  return annualPlanPrice;
};

export const getAnnualPlanPriceString = (priceList: PriceItemInterface[]) => {
  const annualPlanPrice = getAnnualPlanPrice(priceList);

  const p1 = Number(annualPlanPrice.p1) ? annualPlanPrice.p1 : 0;
  const p3 = Number(annualPlanPrice.p3) ? annualPlanPrice.p3 : 0;
  const parkhaus = Number(annualPlanPrice.parkhaus) ? annualPlanPrice.parkhaus : 0;

  let annualPriceString = "";
  if (p1) annualPriceString = `${p1}`;
  if (parkhaus) annualPriceString = `${annualPriceString}/${parkhaus}`;
  if (p3) annualPriceString = `${annualPriceString}/${p3}`;

  return annualPriceString;
};

export const findMaxHours = (priceList: PriceItemInterface[]) => {
  const dayItems = priceList.filter((priceItem) => priceItem.parkdauer.match(/\dh/g));

  return dayItems.reduce((prev, curr) => {
    const currDuration = Number(curr.parkdauer.replace("h", ""));
    return Math.max(prev, currDuration);
  }, -1);
};

export const getHighestPrice = (priceList: PriceItemInterface[]) => {
  const annualPlanPrice = getAnnualPlanPrice(priceList);

  const p1 = typeof annualPlanPrice.p1 === "number" ? annualPlanPrice.p1 : 0;
  const parkhaus = typeof annualPlanPrice.parkhaus === "number" ? annualPlanPrice.parkhaus : 0;
  const p3 = typeof annualPlanPrice.p3 === "number" ? annualPlanPrice.p3 : 0;
  return Math.max(p1, parkhaus, p3);
};

export const getPriceForDuration = (priceList: PriceItemInterface[], [hours, days, weeks]: HoursDaysWeeks) => {
  const prices: PricesInterface = {
    p1: 0,
    parkhaus: 0,
    p3: 0,
  };

  let calculatedBefore = false;

  if (weeks) {
    if (weeks >= 52) {
      const priceToAddYear = findPricesInPriceEntries(priceList, "1y");
      if (!priceToAddYear) throw "Angebot nicht verfügbar";

      addPrices(prices, priceToAddYear);

      return prices;
    }

    const priceToAddWeeks = findPricesInPriceEntries(priceList, "1w");
    if (!priceToAddWeeks) throw "Angebot nicht verfügbar";

    addPrices(prices, priceToAddWeeks);

    if (weeks > 1) {
      for (let i = 0; i < weeks - 1; i++) {
        const pricePerAdditionalWeek = findPricesInPriceEntries(priceList, "w+1");
        if (!pricePerAdditionalWeek) throw "Angebot nicht verfügbar";

        addPrices(prices, pricePerAdditionalWeek);
      }
    }

    calculatedBefore = true;
  }

  if (days) {
    if (!calculatedBefore) {
      const priceToAddDays = findPricesInPriceEntries(priceList, `${days}d`);
      if (!priceToAddDays) throw "Angebot nicht verfügbar";

      addPrices(prices, priceToAddDays);
    } else {
      for (let i = 0; i < days; i++) {
        const pricePerAdditionalDays = findPricesInPriceEntries(priceList, "d+1");
        if (!pricePerAdditionalDays) throw "Angebot nicht verfügbar";

        addPrices(prices, pricePerAdditionalDays);
      }
    }
  }

  if (hours) {
    const priceToAddHours = findPricesInPriceEntries(priceList, `${hours}h`);

    if (!priceToAddHours) {
      const priceOfOneDay = findPricesInPriceEntries(priceList, `1d`);
      if (!priceOfOneDay) throw "Angebot nicht verfügbar";

      addPrices(prices, priceOfOneDay);
    } else {
      addPrices(prices, priceToAddHours);
    }
  }

  return prices;
};

export const updatePricesBasedOnDateRange = (prices: PricesInterface, begin: Date, end: Date) => {
  const p1Range = timeRanges["p1"] as null | { begin: number; end: number };
  const p3Range = timeRanges["p3"] as null | { begin: number; end: number };
  const parkhausRange = timeRanges["parkhaus"] as null | { begin: number; end: number };

  if (p1Range && !(begin.getMonth() >= p1Range.begin && begin.getMonth() <= p1Range.end && end.getMonth() >= p1Range.begin && end.getMonth() <= p1Range.end)) {
    prices.p1 = 0;
  }

  if (p3Range && !(begin.getMonth() >= p3Range.begin && begin.getMonth() <= p3Range.end && end.getMonth() >= p3Range.begin && end.getMonth() <= p3Range.end)) {
    prices.p3 = 0;
  }

  if (
    parkhausRange &&
    !(
      begin.getMonth() >= parkhausRange.begin &&
      begin.getMonth() <= parkhausRange.end &&
      end.getMonth() >= parkhausRange.begin &&
      end.getMonth() <= parkhausRange.end
    )
  ) {
    prices.parkhaus = 0;
  }
};
